/**
 * PagePicker — small popover that lets a user pick a page from the workspace.
 *
 * Phase 2 ships a flat list (the cmdk palette is wired in Phase 4). The
 * `attachToTask` mutation is the canonical consumer — the task drawer calls
 * it when the user clicks "Add page" in the "Referenced in" row.
 */

import { useEffect, useRef, useState } from 'react'
import { usePageTree } from '@/features/notebook/pages'
import { cn } from '@/lib/utils'

export interface PagePickerProps {
  wsId: string
  value?: string | null
  onPick: (pageId: string) => void
  excludeIds?: string[]
  placeholder?: string
}

export function PagePicker({ wsId, value, onPick, excludeIds = [], placeholder = 'Pick a page…' }: PagePickerProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const tree = usePageTree(wsId)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (e.target instanceof Node && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pages = (tree.data ?? []).filter((p) => !p.archivedAt && !excludeIds.includes(p.id))

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded border border-dashed border-surface-300 px-2 py-0.5 text-xs text-surface-500 hover:border-brand-500 hover:text-brand-600 dark:border-surface-700"
      >
        <span className="text-[10px]">+</span> {placeholder}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-72 rounded-md border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-800">
          <div className="max-h-60 overflow-auto">
            {pages.length === 0 && <div className="px-3 py-2 text-xs text-surface-500">No pages yet.</div>}
            {pages.map((p) => {
              const indent = '· '.repeat(p.depth)
              const isCurrent = p.id === value
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    onPick(p.id)
                    setOpen(false)
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-100 dark:hover:bg-surface-700/60',
                    isCurrent && 'font-semibold',
                  )}
                >
                  <span className="text-sm">{p.icon ?? '📄'}</span>
                  <span className="min-w-0 flex-1 truncate">
                    {indent}
                    {p.title}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
